<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { CircleHelp } from 'lucide-vue-next'

interface Props {
  stripeKey: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  ready: [stripe: any, elements: { cardNumber: any; cardExpiry: any; cardCvc: any }]
  error: [message: string]
}>()

const cardNumberElement = ref<any>(null)
const cardExpiryElement = ref<any>(null)
const cardCvcElement = ref<any>(null)
const stripe = ref<any>(null)
const cardholderName = ref('')
const cardNumberError = ref('')
const cardExpiryError = ref('')
const cardCvcError = ref('')
const isLoading = ref(true)

const elementStyle = {
  base: {
    fontSize: '16px',
    color: '#1f2937',
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
    '::placeholder': {
      color: '#9ca3af',
    },
  },
  invalid: {
    color: '#dc2626',
    iconColor: '#dc2626',
  },
}

onMounted(async () => {
  if (!window.Stripe) {
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/'
    script.async = true
    script.onload = initializeStripe
    document.head.appendChild(script)
  } else {
    initializeStripe()
  }
})

function initializeStripe() {
  stripe.value = window.Stripe(props.stripeKey)
  const elements = stripe.value.elements()

  // Card Number Element
  cardNumberElement.value = elements.create('cardNumber', {
    style: elementStyle,
    placeholder: '1234 5678 9012 3456',
    showIcon: true,
  })
  cardNumberElement.value.mount('#stripe-card-number')
  cardNumberElement.value.on('change', (event: any) => {
    cardNumberError.value = event.error ? event.error.message : ''
    emitError()
  })

  // Card Expiry Element
  cardExpiryElement.value = elements.create('cardExpiry', {
    style: elementStyle,
    placeholder: 'MM / YY',
  })
  cardExpiryElement.value.mount('#stripe-card-expiry')
  cardExpiryElement.value.on('change', (event: any) => {
    cardExpiryError.value = event.error ? event.error.message : ''
    emitError()
  })

  // Card CVC Element
  cardCvcElement.value = elements.create('cardCvc', {
    style: elementStyle,
    placeholder: '123',
  })
  cardCvcElement.value.mount('#stripe-card-cvc')
  cardCvcElement.value.on('change', (event: any) => {
    cardCvcError.value = event.error ? event.error.message : ''
    emitError()
  })

  // Mark as ready when all elements are mounted
  cardCvcElement.value.on('ready', () => {
    isLoading.value = false
    emit('ready', stripe.value, {
      cardNumber: cardNumberElement.value,
      cardExpiry: cardExpiryElement.value,
      cardCvc: cardCvcElement.value,
    })
  })
}

function emitError() {
  const error = cardNumberError.value || cardExpiryError.value || cardCvcError.value
  emit('error', error)
}

// Expose for parent component
defineExpose({
  stripe,
  cardNumberElement,
  cardExpiryElement,
  cardCvcElement,
  cardholderName,
})
</script>

<template>
  <section class="bg-white rounded-2xl shadow-sm p-4 md:p-8">
    <h2 class="text-xl font-semibold text-gray-900 mb-6">Card Details</h2>
    <form class="space-y-6" @submit.prevent>
      <!-- Cardholder Name -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-2">Cardholder Name</label>
        <input
          v-model="cardholderName"
          type="text"
          placeholder="John Smith"
          class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-4 text-gray-400 text-sm">
        Loading payment form...
      </div>

      <!-- Card Number -->
      <div v-show="!isLoading">
        <label class="block text-sm font-semibold text-gray-700 mb-2">Card Number</label>
        <div class="relative">
          <div
            id="stripe-card-number"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent min-h-[50px]"
          ></div>
          <div class="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
            <svg class="w-6 md:w-8 h-6 text-blue-600" viewBox="0 0 48 32" fill="currentColor">
              <rect width="48" height="32" rx="4" fill="#1A1F71"/>
              <text x="24" y="20" text-anchor="middle" fill="white" font-size="10" font-weight="bold">VISA</text>
            </svg>
            <svg class="w-6 md:w-8 h-6" viewBox="0 0 48 32">
              <rect width="48" height="32" rx="4" fill="#f0f0f0"/>
              <circle cx="18" cy="16" r="8" fill="#EB001B"/>
              <circle cx="30" cy="16" r="8" fill="#F79E1B"/>
              <path d="M24 10a8 8 0 0 0 0 12 8 8 0 0 0 0-12z" fill="#FF5F00"/>
            </svg>
          </div>
        </div>
        <p v-if="cardNumberError" class="mt-1 text-sm text-red-600">{{ cardNumberError }}</p>
      </div>

      <!-- Expiry and CVV -->
      <div v-show="!isLoading" class="grid grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">Expiry Date</label>
          <div
            id="stripe-card-expiry"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent min-h-[50px]"
          ></div>
          <p v-if="cardExpiryError" class="mt-1 text-sm text-red-600">{{ cardExpiryError }}</p>
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">CVV</label>
          <div class="relative">
            <div
              id="stripe-card-cvc"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent min-h-[50px]"
            ></div>
            <div class="absolute right-4 top-1/2 transform -translate-y-1/2">
              <CircleHelp class="w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-600" />
            </div>
          </div>
          <p v-if="cardCvcError" class="mt-1 text-sm text-red-600">{{ cardCvcError }}</p>
        </div>
      </div>

      <!-- Security Note -->
      <div class="flex items-center gap-2 text-xs text-gray-500 pt-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <span>Your card details are securely processed by Stripe</span>
      </div>
    </form>
  </section>
</template>
